/* Context + the hook that reads it, so the fast-refresh component-only rule
   does not apply here. */
/* eslint-disable react-refresh/only-export-components */
import {createContext, useContext, useMemo} from 'react';

import type {FillAudience} from '../lib/client-fillable';

/**
 * What is true of the whole fill SURFACE, as opposed to any one slot.
 *
 * `LivePreview` scopes a slot's plumbing to its id through `fillProps(id)` —
 * its value, its change handlers, whether it is locked. Three things were
 * riding in that per-slot payload without ever varying by id: who is looking,
 * and the two signer defaults. Carrying a surface constant in a per-slot
 * factory meant every consumer had to be handed it, so it was threaded through
 * seven component layers — and `TableElement` ended up receiving the same value
 * by TWO routes, once inside `cellFill(key)` for the fill branch and once as
 * its own prop for the static branch, because the two halves of one ternary
 * could not share it any other way.
 *
 * It also made the value forgettable: a render path that did not spread
 * `fillProps` silently got the default, which for the audience meant showing a
 * client the crew's word for their own field.
 *
 * So it lives here instead. One provider at the top of the preview, read by the
 * two components that actually care.
 */
export interface FillSurface {
  /**
   * Whose half of the document this surface is showing (DERP-247).
   *
   * The crew's canvas and the public signing link render the same template to
   * different people, and the difference is exactly one fact. Stated as an
   * audience rather than as the words to print, so the wording lives with the
   * component that draws it and a caller cannot set the fillable slots for one
   * side and the labels for the other.
   */
  audience: FillAudience;
  /**
   * Signer-name choices for signature fields, in the order they are offered.
   * Empty on a surface with no roster to offer — including the public link,
   * where the signer is not in any directory the app can list.
   */
  signerOptions: readonly string[];
  /** Pre-filled signer name for a freshly drawn signature, when there is one. */
  defaultSignerName?: string;
  /**
   * This surface is the document itself, not the app showing it (DERP-237).
   *
   * The PDF export rasterises the same canvas rather than rebuilding the report
   * for paper, so everything the screen draws lands on the page — including the
   * chrome that is ours rather than the document's. Whose half a slot belongs to
   * is exactly that: our routing, and it has no business on paper a client
   * signs. Contrast `required`, which IS document content — it was authored into
   * the template — and stays.
   *
   * A surface fact rather than an audience: the export is read by the same two
   * audiences the screen is, and `FillAudience` mirrors the API's
   * `missing_for(audience=…)`, which has no third value to mean "printed".
   */
  forExport?: boolean;
}

/**
 * The crew's canvas, which is what an unwrapped preview is.
 *
 * The editor renders templates with no fill wiring at all, so a default that
 * means "the crew are looking at this" keeps every one of those call sites
 * unchanged. The public link is the surface that has to say so, and it does.
 */
const CREW_SURFACE: FillSurface = {audience: 'crew', signerOptions: []};

const FillSurfaceContext = createContext<FillSurface>(CREW_SURFACE);

export function FillSurfaceProvider({
  audience,
  signerOptions,
  defaultSignerName,
  forExport,
  children,
}: {
  audience?: FillAudience;
  signerOptions?: readonly string[];
  defaultSignerName?: string;
  forExport?: boolean;
  children: React.ReactNode;
}) {
  const value = useMemo<FillSurface>(
    () => ({
      audience: audience ?? 'crew',
      signerOptions: signerOptions ?? [],
      defaultSignerName,
      forExport,
    }),
    [audience, signerOptions, defaultSignerName, forExport],
  );
  return <FillSurfaceContext.Provider value={value}>{children}</FillSurfaceContext.Provider>;
}

/** What is true of the surface this element is being drawn on. */
export function useFillSurface(): FillSurface {
  return useContext(FillSurfaceContext);
}

/**
 * What to call a client-owned slot, to whoever is looking at it.
 *
 * The same slot is "the client's" to the crew and "yours" to the client. Being
 * told a field belongs to "the client" makes the client work out that it means
 * them, which is a small thing to get wrong on a document someone is signing.
 */
export function clientSlotLabelFor(audience: FillAudience): string {
  return audience === 'client' ? 'YOURS' : 'CLIENT';
}
